/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

/** @jsx jsx */
import { jsx } from '@emotion/core'
import PropTypes from 'prop-types'
import uuid from 'uuid/v4'
import hex2rgba from 'hex2rgba'
import Observer from '@researchgate/react-intersection-observer'

import { colors } from '../../constants'
import { createRef, PureComponent } from 'react'

// MODES
export const VIDEO_CLIP_AUTOPLAY = 'VIDEO_CLIP_AUTOPLAY'
export const VIDEO_CLIP_PLAYPAUSE = 'VIDEO_CLIP_PLAYPAUSE'

// PLAYPAUSE STATES
const PLAYPAUSE_HIDE = 'PLAYPAUSE_HIDE'
const PLAYPAUSE_SHOW = 'PLAYPAUSE_SHOW'

// EVENTS
const MOUSE_ENTER_EVENT = 'MOUSE_ENTER_EVENT'
const MOUSE_LEAVE_EVENT = 'MOUSE_LEAVE_EVENT'

const VIDEO_PLAY_EVENT = 'VIDEO_PLAY_EVENT'
const VIDEO_PAUSE_EVENT = 'VIDEO_PAUSE_EVENT'

// VIDEO STATES
const VIDEO_PLAYING = 'VIDEO_PLAYING'
const VIDEO_PAUSED = 'VIDEO_PAUSED'

// AUDIO STATES
const AUDIO_MUTED = 'AUDIO_MUTED'
const AUDIO_UNMUTED = 'AUDIO_UNMUTED'

const propTypes = {
	mode: PropTypes.oneOf([
		/*
			The video will begin playing once the component has crossed into the
			viewport via `video.play()`. Note: The `autoplay` attribute isn't used
			since browsers handle it inconsistently. The element will be initially
			muted for browsers to allow `play()` without a user interaction event. The
			video will continue to play regardless of `activeMediaInstanceID`
		*/
		VIDEO_CLIP_AUTOPLAY,
		/*
			The element includes play/pause controls in an overlay. If the
			`activeMediaInstanceID` prop is different than the video's
			`videoInstanceID`, the video will pause.
		*/
		VIDEO_CLIP_PLAYPAUSE,
	]),
	// The maximum width of the video (will scale down, but never above this
	// value)
	width: PropTypes.number,
	// The maximum height of the video
	height: PropTypes.number,
	/*
		Is the video known to have audio?
		* `true`
		  * Instructs the component to include a mute/unmute control
		  * `VIDEO_CLIP_AUTOPLAY` components always begin mute, and always display
			the control
		  * `VIDEO_CLIP_PLAYPAUSE`
		* `false`
		  * No mute/unmute control
	*/
	hasAudio: PropTypes.bool,
	// (videoInstanceID) => {}
	// Used globally to prevent multiple videos from playing at the same time
	onPlayMediaInstance: PropTypes.func,
	// The videoInstanceID of the video playing globally.
	activeMediaInstanceID: PropTypes.string,
	children: PropTypes.node,
}

const defaultProps = {
	mode: VIDEO_CLIP_AUTOPLAY,
	width: 960,
	height: 540,
	hasAudio: false,
	children: [],
	onPlayMediaInstance: () => {},
	poster: false,
}

class VideoLoop extends PureComponent {
	constructor(props) {
		super(props)
		this.videoEl = createRef()

		// Used to prevent multiple video from playing at once
		this.videoInstanceID = uuid()

		const playPauseState =
			props.mode === VIDEO_CLIP_AUTOPLAY ? PLAYPAUSE_HIDE : PLAYPAUSE_SHOW

		const muteState =
			props.mode === VIDEO_CLIP_AUTOPLAY ? AUDIO_MUTED : AUDIO_UNMUTED

		this.state = {
			playPauseState,
			videoState: VIDEO_PAUSED,
			videoLoaded: false,
			hasIntersectedViewport: false,
			muteState,
		}
	}

	componentDidUpdate(prevProps) {
		const { activeMediaInstanceID, mode, hasAudio } = this.props

		if (mode === VIDEO_CLIP_PLAYPAUSE) {
			// Did another external video start playing?
			if (
				activeMediaInstanceID &&
				activeMediaInstanceID !== this.videoInstanceID
			) {
				// the pause this one
				this.videoEl.current.pause()
			}
		}

		// If the component prevous had audio, but now does not, mute that thang!
		if (prevProps.hasAudio && !hasAudio) {
			this.videoEl.current.muted = true
		}
	}

	handleIntersection = ({ isIntersecting }, unobserve) => {
		if (isIntersecting && !this.state.hasIntersectedViewport) {
			this.setState({
				hasIntersectedViewport: true,
			})
			if (this.props.mode === VIDEO_CLIP_AUTOPLAY) {
				this.videoEl.current.play()
			}
			unobserve()
		}
	}

	onMouseEnter = () => {
		this.dispatchEvent(MOUSE_ENTER_EVENT)
	}

	onMouseLeave = () => {
		this.dispatchEvent(MOUSE_LEAVE_EVENT)
	}

	onLoadedData = () => {
		this.setState({
			videoLoaded: true,
		})
	}

	onVideoPlay = () => {
		this.setState({
			videoState: VIDEO_PLAYING,
		})
		this.dispatchEvent(VIDEO_PLAY_EVENT)
		if (this.props.mode === VIDEO_CLIP_PLAYPAUSE) {
			this.props.onPlayMediaInstance(this.videoInstanceID)
		}
	}

	onVideoPause = () => {
		this.setState({
			videoState: VIDEO_PAUSED,
		})
		this.dispatchEvent(VIDEO_PAUSE_EVENT)
	}

	onVideoVolumeChange = () => {
		const video = this.videoEl.current
		this.setState({
			muteState: video.muted ? AUDIO_MUTED : AUDIO_UNMUTED,
		})
	}

	onVideoClick = () => {
		if (this.props.mode === VIDEO_CLIP_AUTOPLAY) {
			return
		}

		const video = this.videoEl.current
		if (video.paused || video.ended) {
			video.play()
			return
		}
		video.pause()
	}

	handMuteToggleMute = () => {
		const video = this.videoEl.current
		video.muted = !video.muted
	}

	// Control state machine

	playPauseStateMachine = {
		[PLAYPAUSE_HIDE]: {
			[MOUSE_ENTER_EVENT]: () => {
				// If the video is playing, show the pause button
				if (this.state.videoState === VIDEO_PLAYING) {
					this.transitionPlayPauseState(PLAYPAUSE_SHOW)
				}
			},
			[VIDEO_PAUSE_EVENT]: () => {
				// Show the play button
				this.transitionPlayPauseState(PLAYPAUSE_SHOW)
			},
		},
		[PLAYPAUSE_SHOW]: {
			[MOUSE_LEAVE_EVENT]: () => {
				// If the video is playing Hide the controls
				if (this.state.videoState === VIDEO_PLAYING) {
					this.transitionPlayPauseState(PLAYPAUSE_HIDE)
				}
			},
			[VIDEO_PLAY_EVENT]: () => {
				// Hide the controls
				this.transitionPlayPauseState(PLAYPAUSE_HIDE)
			},
		},
	}

	dispatchEvent = event => {
		const { playPauseState } = this.state
		if (this.playPauseStateMachine[playPauseState][event]) {
			this.playPauseStateMachine[playPauseState][event]()
		}
	}

	transitionPlayPauseState = playPauseState => {
		if (this.props.mode === VIDEO_CLIP_AUTOPLAY) {
			return
		}

		this.setState({
			playPauseState,
		})
	}

	render() {
		const { children, width, height, mode, hasAudio, poster } = this.props

		const {
			playPauseState,
			videoState,
			muteState,
			hasIntersectedViewport,
			videoLoaded,
		} = this.state

		const isAutoPlay = mode === VIDEO_CLIP_AUTOPLAY

		// Note muted isn't set as an attribute.. it's assigned as a DOM property
		// by React. This only sets the INITIAL value
		// https://github.com/facebook/react/blob/master/packages/react-dom/src/shared/DOMProperty.js#L305-L322
		const videoProps = isAutoPlay
			? { loop: true, playsInline: true, muted: true }
			: { playsInline: true, muted: !hasAudio }

		const PlayPauseIconTag = videoState === VIDEO_PLAYING ? PauseIcon : PlayIcon

		const intersectionOpts = {
			onChange: this.handleIntersection,
			rootMargin: '0% 0% -25%',
		}

		const showMuteControl = isAutoPlay
			? videoLoaded && hasAudio && videoState === VIDEO_PLAYING
			: videoLoaded && hasAudio && playPauseState === PLAYPAUSE_SHOW

		const hideLoading = videoLoaded && hasIntersectedViewport

		return (
			<Observer {...intersectionOpts}>
				<div
					data-comp-video-clip
					role="img"
					onMouseEnter={this.onMouseEnter}
					onMouseLeave={this.onMouseLeave}
					css={{
						position: 'relative',
						maxWidth: width,
						overflow: 'hidden',
					}}
				>
					<video
						ref={this.videoEl}
						controls={false}
						onPlay={this.onVideoPlay}
						onPause={this.onVideoPause}
						onClick={this.onVideoClick}
						onLoadedData={this.onLoadedData}
						onVolumeChange={this.onVideoVolumeChange}
						css={{
							display: 'block',
							zIndex: 0,
							position: 'absolute',
							width: '100%',
							height: '100%',
							top: 0,
							left: 0,
							cursor: isAutoPlay ? 'default' : 'pointer',
						}}
						{...(poster ? { poster } : {})}
						{...videoProps}
					>
						{children}
					</video>
					<button
						aria-label={
							videoState === VIDEO_PLAYING ? 'Pause video' : 'Play video'
						}
						onClick={this.onVideoClick}
						css={{
							zIndex: 1,
							position: 'absolute',
							width: '100%',
							height: '100%',
							top: 0,
							left: 0,
							margin: 0,
							display: isAutoPlay ? 'none' : 'block',
							border: 0,
							padding: 0,
							cursor: 'pointer',
							pointerEvents:
								playPauseState === PLAYPAUSE_SHOW ? 'auto' : 'none',
							opacity: playPauseState === PLAYPAUSE_SHOW ? 1 : 0,
							transition: 'all 150ms linear',
							background: hex2rgba(colors.ink, 0.2),
						}}
					>
						<div
							css={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								transition: 'all 150ms linear',
								width: '100%',
								height: '100%',
							}}
						>
							<PlayPauseIconTag />
						</div>
					</button>
					<button
						onClick={this.handMuteToggleMute}
						aria-label={
							muteState === AUDIO_MUTED ? 'Unmute video' : 'Mute video'
						}
						css={{
							zIndex: 2,
							position: 'absolute',
							right: '1rem',
							bottom: '1rem',
							display: 'block',
							padding: 0,
							border: 0,
							background: 'none',
							cursor: 'pointer',
							pointerEvents: showMuteControl ? 'auto' : 'none',
							opacity: showMuteControl ? 1 : 0,
							transition: 'opacity 150ms linear',
						}}
					>
						<MuteControlIcon
							icon={muteState === AUDIO_MUTED ? 'mute' : 'unmute'}
							bgColor={
								mode === VIDEO_CLIP_AUTOPLAY
									? 'rgba(255, 255, 255, 0.5)'
									: 'rgba(255, 255, 255, 0)'
							}
							fgColor={mode === VIDEO_CLIP_AUTOPLAY ? colors.ink : '#FFFFFF'}
						/>
					</button>
					<div
						css={{
							// Pass pointer events through
							display: 'flex',
							pointerEvents: 'none',
							zIndex: 3,
							position: 'absolute',
							width: '100%',
							height: '100%',
							top: 0,
							left: 0,
							opacity: hideLoading ? 0 : 1,
							justifyContent: 'center',
							alignItems: 'center',
							background: '#FFFFFF',
							transition: 'opacity 50ms linear',
						}}
					>
						<span className="spinner" aria-label="Busy" role="img" />
					</div>

					<svg css={{ width: '100%' }} viewBox={`0 0 ${width} ${height}`} />
				</div>
			</Observer>
		)
	}
}

VideoLoop.propTypes = propTypes
VideoLoop.defaultProps = defaultProps

export default VideoLoop

const PlayIcon = () => (
	<svg
		css={{
			display: 'block',
		}}
		width="72px"
		height="72px"
		viewBox="0 0 72 72"
	>
		<path
			d="M0,0 L72,0 L72,72 L0,72 L0,0 Z M46,36 L31,25 L31,46 L46,36 Z"
			fill="#FFFFFF"
		/>
	</svg>
)

const PauseIcon = () => (
	<svg
		css={{
			display: 'block',
		}}
		width="72px"
		height="72px"
		viewBox="0 0 72 72"
	>
		<path
			d="M0,0 L73,0 L73,73 L0,73 L0,0 Z M44.4701388,46.2446925 L44.4701388,27.7604003 L38.2836488,27.7604003 L38.2836488,46.2446925 L44.4701388,46.2446925 Z M34.675161,46.2446925 L34.675161,27.7604003 L28.488671,27.7604003 L28.488671,46.2446925 L34.675161,46.2446925 Z"
			fill="#FFFFFF"
		/>
	</svg>
)

const MuteControlIcon = ({ icon, bgColor, fgColor }) => (
	<div
		css={{
			width: '2rem',
			height: '2rem',
			fontSize: '1rem',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			background: bgColor,
			borderRadius: '4rem',
			color: fgColor,
		}}
	>
		{/* <Icon icon={icon} /> */}
	</div>
)
